/**
 * The axis spine: one row's rubric score broken into its six axes, as a single bar.
 *
 * This is the one new visual idiom on the page, and it exists because `axis_scores`
 * has been fetched and never rendered. A target's score is a weighted mean over the
 * axes that were *measured*, so the same 72 can mean "solid across the whole rubric"
 * or "excellent on the two things anybody looked at". The score alone cannot tell
 * those apart; the spine shows both at once.
 *
 * Each segment is one axis:
 *
 * - **width ∝ the axis's weight**, so the bar's filled area is score × coverage —
 *   the two numbers the tier bands are actually cut on.
 * - **fill height ∝ the axis score**, 0–100, in Corteva blue.
 * - **an unmeasured axis is hollow**, a hairline outline with no fill. Never a
 *   zero-height fill. A zero fill says "measured, scored nothing"; a hollow segment
 *   says "nobody measured this". They are opposite findings and this repo has
 *   shipped the confusion before.
 *
 * The score fills each segment's *height* rather than partially filling its width,
 * which is a legibility decision and not an arbitrary one. Filled area encodes
 * score × coverage either way, but Safety carries weight 5, so its segment is about
 * six pixels wide in a table cell — a 60% horizontal fill of that is under four
 * pixels and reads as noise, while a column of 60% height reads at any width. The
 * side effect is that the spine also reads as a six-bar chart, which is what makes
 * *comparing* axes within a row possible at all.
 *
 * Read down a column of rows it becomes texture: dense rows are well-evidenced,
 * gappy rows are speculative, and which *axis* is missing is visible from the
 * position of the gap. That is invisible in a score column.
 *
 * The axis set is **not** hardcoded. Names and weights come from
 * `generated/wiki.json`, which `scripts/build-wiki.mjs` parses out of
 * `context/target-criteria` §4 at build time — the same file the ranker reads. A
 * fixed list here would be a second rubric, and it would drift the first time an
 * axis was re-weighted with nothing to say which copy produced a given bar. The
 * Python side does exactly this too, in `plot_tools.RUBRIC_COMPONENTS`: names where
 * the code needs them, weights only from the rubric.
 */
import wiki from '../../generated/wiki.json'
import { cn } from '../../lib/utils'

/**
 * The rubric as segments, computed once at module load.
 *
 * `axis_scores` is keyed lowercase (`essentiality`) while the rubric table titles
 * its axes (`Essentiality`), so the join is a case fold. Whitespace is folded to `_`
 * as well, against an axis being renamed to two words.
 */
const SEGMENTS = wiki.rubric.map((axis) => ({
  label: axis.axis,
  key: axis.axis.trim().toLowerCase().replace(/\s+/g, '_'),
  weight: axis.weight,
}))

const KNOWN_KEYS = new Set(SEGMENTS.map((s) => s.key))

interface Props {
  /** One key per rubric axis, 0–100, `null` where the axis was not measured. */
  axisScores: Record<string, number | null> | null | undefined
  className?: string
}

export function AxisSpine({ axisScores, className }: Props) {
  // Nothing was recorded — a row written before the migration, a board row (which
  // carries coverage but no breakdown), or a write whose payload never carried the
  // axes. Not the same as every axis being null, and the caller should be showing a
  // coverage meter instead.
  //
  // An **empty object counts as nothing recorded**, and that half of the check is
  // why this branch is reachable at all: `axis_scores` is `jsonb NOT NULL DEFAULT
  // '{}'` (`infra/sql/012_score_and_coverage.sql`) and the API is a `SELECT *`
  // pass-through, so a run row the writer stored no axes for arrives as `{}` —
  // truthy. `!axisScores` alone was dead code for every run row, and `{}` fell
  // through to six hollow segments, which is exactly the "zero fill vs hollow"
  // confusion this file's own header calls opposite findings. `run-0fac0cef` shipped
  // seven rows of it: `score_source=absent` on all seven, rendering identically to a
  // rubric that had been applied and measured nothing, beside a real Elo of 1666.
  if (!axisScores || Object.keys(axisScores).length === 0) {
    return (
      <span className="text-2xs text-slate-500" title="No per-axis breakdown was stored for this row. Its score and coverage, where present, still hold — only the split between axes is missing.">
        No breakdown
      </span>
    )
  }

  // Keys the rubric no longer has. Surfaced rather than dropped: if §4 renames an
  // axis, every spine on the page goes hollow at once, which looks exactly like a
  // sweep that measured nothing. This is the line that says otherwise.
  const orphans = Object.keys(axisScores).filter((k) => !KNOWN_KEYS.has(k))

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <div
        className="flex h-4 w-full min-w-[7rem] items-stretch gap-px"
        role="img"
        aria-label={describe(axisScores)}
      >
        {SEGMENTS.map((seg) => {
          const raw = axisScores[seg.key]
          const measured = raw !== null && raw !== undefined
          const pct = measured ? Math.max(0, Math.min(100, raw)) : 0
          return (
            <div
              key={seg.key}
              // `flexGrow` from the weight, so the segments' widths *are* the
              // rubric. Hardcoding six column widths would put the weights in a
              // second place.
              style={{ flexGrow: seg.weight }}
              title={`${seg.label} · weight ${seg.weight} · ${
                measured ? `scored ${Math.round(pct)}` : 'not measured'
              }`}
              className={cn(
                'group/seg relative flex min-w-[3px] items-end overflow-hidden rounded-sm transition-transform',
                measured
                  ? 'bg-slate-800 hover:-translate-y-px'
                  : 'border border-dashed border-slate-700',
              )}
            >
              {measured && (
                <div
                  className="w-full rounded-sm bg-brand-500"
                  style={{ height: `${pct}%` }}
                />
              )}
            </div>
          )
        })}
      </div>
      {orphans.length > 0 && (
        <span
          className="shrink-0 font-mono text-2xs text-amber-400"
          title={`Scored against axes this build's rubric does not have: ${orphans.join(', ')}. context/target-criteria §4 was changed after this row was written, so the spine cannot place these scores — the row's own score and coverage are still the ones it was ranked under.`}
        >
          +{orphans.length}
        </span>
      )}
    </div>
  )
}

/**
 * The spine in words, for a screen reader and for anyone who cannot hover.
 *
 * Names the unmeasured axes explicitly instead of reporting a coverage percentage:
 * *which* axis is missing is the actionable half, and it is the half a number drops.
 */
function describe(scores: Record<string, number | null>): string {
  const parts = SEGMENTS.map((seg) => {
    const raw = scores[seg.key]
    return raw === null || raw === undefined
      ? `${seg.label} not measured`
      : `${seg.label} ${Math.round(raw)}`
  })
  return `Rubric axes: ${parts.join(', ')}.`
}

/** The rubric's axes, heaviest first as authored. For a table's column headings. */
export const SPINE_AXES = SEGMENTS
